import { useContext } from 'react';
import API from '../../api';
import ENDPOINTS from '../../endpoints';
import GlobalContext from '../../contexts/GlobalContext';
import {
  MutationResultPair,
  QueryResult,
  useMutation,
  useQuery
} from 'react-query';
import { AppDataList, Tenure } from '../../types/common';

const useAppData = (options: {
  onSuccess?: (data: AppDataList) => void;
  onError?: (err: unknown) => void;
}): QueryResult<AppDataList> => {
  const { globalContext } = useContext(GlobalContext);
  const endPoints = ENDPOINTS();
  let appDataList: AppDataList;
  const fetchAppData = async (): Promise<AppDataList> => {
    let appDataUrl = `${endPoints.appData()}?cluster=${globalContext.gb.utils.getClusterValue()}`;
    const { status, data } = await API.get(appDataUrl, {
      method: 'GET',
      headers: {
        'content-type': 'application/json'
      }
    });

    if (status === 200 && data && data.appData) {
      appDataList = data.appData;
    }

    return appDataList;
  };

  return useQuery('appData', fetchAppData, {
    enabled: globalContext.customer && globalContext.customer.uuid,
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    onSuccess: options.onSuccess,
    onError: options.onError
  });
};

export const useUpdateAppData = (options?: {
  onSuccess?: (data: AppDataList) => void;
}): MutationResultPair<AppDataList, unknown, AppDataList, unknown> => {
  const { globalContext } = useContext(GlobalContext);
  const endPoints = ENDPOINTS();
  let appDataList: AppDataList;
  const updateAppData = async (payload: AppDataList): Promise<AppDataList> => {
    let appDataUrl = `${endPoints.appData()}?cluster=${globalContext.gb.utils.getClusterValue()}&createAppData=true`;
    const { status, data } = await API.put(appDataUrl, payload);

    if (status === 200 && data && data.appData) {
      appDataList = data.appData;
    }

    return appDataList;
  };

  return useMutation((payload: AppDataList) => updateAppData(payload), {
    onSuccess: options?.onSuccess
  });
};

export const useTenure = (options?: {
  onSuccess?: (data: Tenure) => void;
}): QueryResult<Tenure> => {
  const { globalContext } = useContext(GlobalContext);
  const endPoints = ENDPOINTS();
  let tenureData: Tenure;
  const fetchTenure = async (): Promise<Tenure> => {
    let tenureUrl = `${endPoints.tenure()}?cluster=${globalContext.gb.utils.getClusterValue()}`;
    const { status, data } = await API.get(tenureUrl, {
      method: 'GET',
      headers: {
        'content-type': 'application/json'
      }
    });

    if (status === 200 && data) {
      tenureData = data;
    }

    return tenureData;
  };

  return useQuery('tenure', fetchTenure, {
    enabled: globalContext.customer && globalContext.customer.uuid,
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    onSuccess: options.onSuccess
  });
};

export default useAppData;
